import { appAccent } from '@infinix/tokens';
import type { AppEntry } from './index';

/**
 * The app catalog: what the launcher needs to know about every app, and nothing more. Each app keeps its own
 * menu (nav) in its own codebase. Adding an app = one entry here + its own deployable (docs/APP-CATALOG.md).
 * Paths are a public contract between apps. Product apps (SmartBill, Retail, Giving, Stay) are added when they
 * adopt the shell (plan step 7).
 */
export const CATALOG: AppEntry[] = [
  {
    id: 'ops', name: 'Ops', description: 'Nest staff: tenants, onboarding, providers, platform health and support',
    icon: 'ops', accent: { light: appAccent.ops, dark: '#2FC9B4' }, basePath: '/ops', audience: 'staff', anyPermission: ['ops.tenants.read'],
  },
  {
    id: 'payments', name: 'Payments', description: 'Every payment across products: money in, money out and control', icon: 'payments',
    accent: { light: appAccent.payments, dark: '#7C6BFF' }, basePath: '/payments', audience: 'customer', anyPermission: ['payments.read'],
    // Staff open Payments across tenants, or one tenant (A-22, A-32), once Core's staff route set is live (N-7).
    staffScope: { anyPermission: ['payments.platform.read'], allTenants: true },
  },
  {
    id: 'org', name: 'Organization', description: 'Your organization, business units, people and access', icon: 'org',
    accent: { light: appAccent.org, dark: '#6EA8FF' }, basePath: '/org', audience: 'customer', anyPermission: ['org.read'],
    // Staff open one tenant's organization (A-22, A-32, chunk 6): it's where picking an organization lands (A-35).
    staffScope: { anyPermission: ['ops.tenants.read'] },
  },
  {
    // Product app (owner, 2026-10-19: SmartBill first). Bills and collecting a bill; the money itself is in Payments.
    // Staff read it in the staff view once they pick an organization that runs SmartBill (A-35).
    id: 'smartbill', name: 'SmartBill', description: 'Bills, collecting a bill, payers and how they verify', icon: 'smartbill',
    accent: { light: appAccent.smartbill, dark: '#2FB5C0' }, basePath: '/smartbill', audience: 'customer', anyPermission: ['smartbill.read'],
    product: 'SmartBill', staffScope: { anyPermission: ['ops.tenants.read'] },
    // The ECOM bills a payment pays, as SmartBill's flows put them on it (IX_Retail source, IX_Payments on gani data):
    // collecting from a bill sends invoiceIds; older pay links carry invoiceId (one ECOM bill id). Online payments carry
    // neither (ECOM resolves their bills at capture): SmartBill finds them by the payment (ECOM #189).
    records: [
      { ref: 'invoiceIds', label: 'Bill', path: '/smartbill/bills/:id' },
      { ref: 'invoiceId', label: 'Bill', path: '/smartbill/bills/:id' },
    ],
    paymentRecords: { label: 'Bills paid', path: '/smartbill/payments/:id' },
  },
  {
    id: 'account', name: 'My account', description: 'Your profile, sign-in and security, notifications and regional settings', icon: 'account',
    accent: { light: appAccent.account, dark: '#98A2B3' }, basePath: '/account', audience: 'customer', anyPermission: [], everyone: true,
  },
];

/** The catalog entry for an app id; an app's manifest spreads this and adds its own nav. */
export function catalogEntry(id: string): AppEntry {
  const e = CATALOG.find((a) => a.id === id);
  if (!e) throw new Error(`App "${id}" is not in the catalog`);
  return e;
}

/** The catalog app that owns a path, if any (used to route cross-app links). */
export function appForPath(pathname: string): AppEntry | undefined {
  return CATALOG.find((a) => pathname === a.basePath || pathname.startsWith(`${a.basePath}/`));
}
